import { useCallback, useReducer, useRef } from "react";

/**
 * The in-house builder's state machines. Each is a table of state -> event -> next state, so
 * every path through the UI is written down in one place and an undesigned one is refused
 * instead of happening quietly. docs/in-house-state-machines.md draws them.
 *
 * Handlers send events; they never set the state. Rendering reads the state.
 */

/** Where an integration is in its life. The server owns it; the UI only asks to move it. */
export const LIFECYCLE = {
  Draft: { publish: "Live", decommission: "Gone" },
  Live: { disable: "Disabled", unpublish: "Draft", decommission: "Gone" },
  Disabled: { turnOn: "Live", unpublish: "Draft", decommission: "Gone" },
  Gone: {}
};

/**
 * Which screen the builder shows. `syncDraft` / `syncPublished` replay the server's status
 * after any load or change, so the view can't disagree with it; they are allowed from every
 * state that has an integration. Leaving (Back, Done, decommission) unmounts the builder.
 */
export const BUILDER_VIEW = {
  loading: { syncDraft: "setup", syncPublished: "manage" },
  start: { scratch: "setup" },
  setup: { published: "celebrate", syncDraft: "setup", syncPublished: "manage" },
  celebrate: { keepEditing: "manage", syncDraft: "setup", syncPublished: "celebrate" },
  manage: { syncDraft: "setup", syncPublished: "manage" }
};

/**
 * One Try it request. `responded` is any HTTP answer from the API, 4xx and 5xx included;
 * `rejected` means Orbit refused or couldn't send it (bad input, turned off, network).
 * A second Send while one is in flight is refused, so a double click sends once.
 */
export const TRY_REQUEST = {
  idle: { send: "sending", reset: "idle" },
  sending: { responded: "responded", rejected: "rejected" },
  responded: { send: "sending", reset: "idle" },
  rejected: { send: "sending", reset: "idle" }
};

/** The state `event` leads to from `state`, or undefined when the table has no such move. */
export const nextState = (table, state, event) => table[state]?.[event];

/** Events allowed from `state`, in the table's order. */
export const allowedEvents = (table, state) => Object.keys(table[state] || {});

/** A refused move is a bug in the caller: loud while developing, silent in production. */
export function reportRefused(name, state, event) {
  if (process.env.NODE_ENV === "production") return;
  const message = `[${name}] refused "${event}" from "${state}"`;
  // eslint-disable-next-line no-console
  console.error(message);
  if (process.env.NODE_ENV !== "development" || typeof document === "undefined") return;
  const toast = document.createElement("div");
  toast.textContent = message;
  toast.setAttribute("role", "alert");
  toast.style.cssText =
    "position:fixed;bottom:16px;left:16px;z-index:9999;padding:8px 12px;border-radius:8px;" +
    "background:#9f1239;color:#fff;font:12px ui-monospace,monospace";
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 6000);
}

/**
 * Runs one table in a component. `send(event)` applies the move and returns whether it was
 * allowed, so a caller can skip its side effect when it wasn't (e.g. a second Send).
 */
export function useMachine(name, table, initial) {
  const state = useRef(initial);
  const [, rerender] = useReducer((n) => n + 1, 0);
  const send = useCallback(
    (event) => {
      const next = nextState(table, state.current, event);
      if (next === undefined) {
        reportRefused(name, state.current, event);
        return false;
      }
      if (next !== state.current) {
        state.current = next;
        rerender();
      }
      return true;
    },
    [name, table]
  );
  return [state.current, send];
}
